import states from '@config/states'
import { geocodeZip } from '@lib/geo-api'
import { firstQueryParam } from '@utils/firstQueryParam'
import { convertFromSlug } from '@utils/slugs'
import {
  parseCityStateFromSearchText,
  PLAY_ZIP_SEARCH_RADIUS_MILES,
  resolveCityStateSearchParamsFromZipCode,
} from '@lib/golf-facilities/play-search-facility-query'
import {
  searchFacilitiesByCityState,
  searchFacilitiesByRadius,
  searchFacilitiesByText,
} from './algolia-facility-search'

export function hasPlaySearchQueryParams(query = {}) {
  const zipTrim = String(firstQueryParam(query.zipCode) ?? '').trim()
  if (zipTrim) return true

  const searchTrim = String(firstQueryParam(query.searchText) ?? '').trim()
  if (searchTrim) return true

  const cityRaw = firstQueryParam(query.city)
  const stateRaw = firstQueryParam(query.state)
  const cityTrim = cityRaw ? String(cityRaw).trim() : ''
  const stateTrim = stateRaw ? String(stateRaw).trim() : ''
  return Boolean(cityTrim && stateTrim)
}

/**
 * Flag-off /play/search: Coach Tools geo + direct Algolia (no pga-algolia-service GraphQL).
 */
export async function searchPlayFacilities(query = {}) {
  const zipTrim = String(firstQueryParam(query.zipCode) ?? '').trim()
  if (zipTrim) {
    const geo = await geocodeZip(zipTrim)
    if (geo?.lat != null && geo?.lng != null) {
      const radiusResults = await searchFacilitiesByRadius(
        geo.lat,
        geo.lng,
        PLAY_ZIP_SEARCH_RADIUS_MILES,
      )
      if (radiusResults.length > 0) return radiusResults
    }

    const cityState = await resolveCityStateSearchParamsFromZipCode(zipTrim)
    if (cityState) {
      const stateName = states[cityState.state]
      if (stateName) {
        const cityResults = await searchFacilitiesByCityState(stateName, cityState.city)
        if (cityResults.length > 0) return cityResults
      }
    }
    return []
  }

  const searchTrim = String(firstQueryParam(query.searchText) ?? '').trim()
  if (searchTrim) {
    const cityState = parseCityStateFromSearchText(searchTrim)
    const stateName = cityState ? states[cityState.state] : undefined
    if (cityState && stateName) {
      return searchFacilitiesByCityState(stateName, cityState.city)
    }
    return searchFacilitiesByText(searchTrim)
  }

  const cityRaw = firstQueryParam(query.city)
  const stateRaw = firstQueryParam(query.state)
  const cityTrim = cityRaw ? String(cityRaw).trim() : ''
  const stateTrim = stateRaw ? String(stateRaw).trim() : ''
  if (!cityTrim || !stateTrim) return null

  const state = String(stateRaw).toUpperCase()
  const stateName = states[state]
  if (!stateName) return []

  return searchFacilitiesByCityState(
    stateName,
    convertFromSlug(String(cityRaw)),
  )
}
